import React, { Ref, forwardRef } from 'react';
import { FaLocationDot } from 'react-icons/fa6';
import { AddressSummary } from './AddressSummary';
import type { GroupedFieldVisibilities, StoreWithDistance } from './Locator';
import { LocatorResponse, TranslationTarget } from '../lib/dto';
import { Label } from './Label';

function styleLinks({ parent, color }: { parent: HTMLElement; color: string }) {
  parent.querySelectorAll('a').forEach((el) => {
    // eslint-disable-next-line no-param-reassign
    el.className += ' simple-store-locator-link';
    // eslint-disable-next-line no-param-reassign
    el.style.color = color;
  });
}

export interface StoreProps {
  store: StoreWithDistance;
  customFields: LocatorResponse['customFields'];
  selected: boolean;
  themeColor: string;
  distanceUnit: LocatorResponse['settings']['distanceUnit'];
  translations: {
    targets: Partial<Record<TranslationTarget, { value: string }>>;
    customFields: Record<string, { value: string }>;
  };
  borderBottom: boolean;
  showDetailsLabels: boolean;
  groupedFieldVisibilities: GroupedFieldVisibilities;
  onClick: () => void;
}

export const Store = forwardRef<HTMLDivElement, StoreProps>(function Store(
  {
    store,
    customFields,
    selected,
    themeColor,
    distanceUnit,
    translations,
    borderBottom,
    showDetailsLabels,
    groupedFieldVisibilities,
    onClick,
  },
  ref: Ref<HTMLDivElement>,
) {
  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        marginLeft: '4px',
        gap: '15px',
        paddingLeft: '15px',
        paddingTop: '10px',
        paddingBottom: '10px',
        boxShadow: selected ? `-4px 0px 0px 0px ${themeColor}` : 'none',
        borderBottom: borderBottom ? '1px solid #e1e3e5' : 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <FaLocationDot fill={themeColor} style={{ fontSize: '20px' }} />
        {store.distance !== null && (
          <div style={{ whiteSpace: 'nowrap' }}>
            {distanceUnit === 'km'
              ? `${Math.round(store.distance / 1000)} km`
              : `${Math.round(store.distance / 1609.34)} mi`}
          </div>
        )}
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={onClick}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
        }}
      >
        <div
          style={{
            cursor: 'pointer',
            color: themeColor,
            fontSize: '15px',
            fontWeight: 700,
          }}
        >
          {store.name}
        </div>
        <div>
          <AddressSummary inline={false} store={store} />
        </div>
        {groupedFieldVisibilities.targets.searchFilters.find(
          (v) => v.scope === 'list' && v.visible === true,
        ) &&
          store.searchFilters.map((searchFilter) => {
            return (
              <div className="simple-store-locator-store-search-filter">
                {searchFilter.name}
              </div>
            );
          })}
        {store.phone &&
          groupedFieldVisibilities.targets.phone.find(
            (v) => v.scope === 'list' && v.visible === true,
          ) && (
            <div style={{ display: 'flex', gap: '5px' }}>
              {showDetailsLabels && (
                <Label>{translations.targets.phone?.value || 'Phone'}:</Label>
              )}
              <a
                href={`tel:${store.phone}`}
                target="_blank"
                rel="noreferrer"
                style={{ wordBreak: 'break-word', color: themeColor }}
                className="simple-store-locator-link"
              >
                {store.phone}
              </a>
            </div>
          )}
        {store.email &&
          groupedFieldVisibilities.targets.email.find(
            (v) => v.scope === 'list' && v.visible === true,
          ) && (
            <div style={{ display: 'flex', gap: '5px' }}>
              {showDetailsLabels && (
                <Label>{translations.targets.email?.value || 'Email'}:</Label>
              )}
              <a
                href={`mailto:${store.email}`}
                target="_blank"
                rel="noreferrer"
                style={{ wordBreak: 'break-word', color: themeColor }}
                className="simple-store-locator-link"
              >
                {store.email}
              </a>
            </div>
          )}
        {store.website &&
          groupedFieldVisibilities.targets.website.find(
            (v) => v.scope === 'list' && v.visible === true,
          ) && (
            <div style={{ display: 'flex', gap: '5px' }}>
              {showDetailsLabels && (
                <Label>
                  {translations.targets.website?.value || 'Website'}:
                </Label>
              )}
              <a
                href={store.website}
                target="_blank"
                rel="noreferrer"
                style={{ wordBreak: 'break-word', color: themeColor }}
                className="simple-store-locator-link"
              >
                {store.website}
              </a>
            </div>
          )}
        {store.notes &&
          groupedFieldVisibilities.targets.notes.find(
            (v) => v.scope === 'list' && v.visible === true,
          ) && (
            <div
              ref={(divRef) => {
                if (!divRef) {
                  return;
                }
                styleLinks({ parent: divRef, color: themeColor });
              }}
            >
              {showDetailsLabels && (
                <div>
                  <Label>{translations.targets.notes?.value || 'Notes'}:</Label>
                </div>
              )}
              <div
                className="simple-store-locator-multiline-value"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: store.notes }}
              />
            </div>
          )}
        {customFields
          .sort((a, b) => a.position - b.position)
          .filter((customField) => {
            return !!groupedFieldVisibilities.customFields[
              customField.id
            ]?.find((v) => v.scope === 'list' && v.visible === true);
          })
          .map((customField) => {
            const customFieldValue = store.customFieldValues.find(
              (fieldValue) => {
                return fieldValue.fieldId === customField.id;
              },
            );
            if (!customFieldValue || !customFieldValue.value) {
              return null;
            }

            if (customField.type === 'plain') {
              return (
                <div style={{ display: 'flex', gap: '5px' }}>
                  {showDetailsLabels && (
                    <Label>
                      {translations.customFields[customField.id]?.value ||
                        customField.name}
                      :
                    </Label>
                  )}
                  <span>{customFieldValue.value}</span>
                </div>
              );
            }

            if (customField.type === 'multiline') {
              return (
                <div>
                  {showDetailsLabels && (
                    <div>
                      <Label>
                        {translations.customFields[customField.id]?.value ||
                          customField.name}
                        :
                      </Label>
                    </div>
                  )}
                  <div
                    ref={(divRef) => {
                      if (!divRef) {
                        return;
                      }
                      styleLinks({ parent: divRef, color: themeColor });
                    }}
                    className="simple-store-locator-multiline-value"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: customFieldValue.value,
                    }}
                  />
                </div>
              );
            }

            return <div />;
          })}
      </div>
    </div>
  );
});
